import React, { useState, useEffect, useRef } from "react";
import { FaBox, FaClock, FaClipboardList, FaCheckCircle } from "react-icons/fa";
import { fetchDashboardCount, fetchOrders, orderStatus, deleteStatus, logoutUser } from "../apis/auth"; // Adjust the import path as necessary
import EditOrderModal from "./editOrderModel"; // Adjust the import path as necessary

const DashboardData = ({ onNewOrder }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [filter, setFilter] = useState("All Orders");
  const [dropdownIndex, setDropdownIndex] = useState(null);
  const [orders, setOrders] = useState([]);
  const [totalOrders, setTotalOrders] = useState(0);
  const dropdownRef = useRef(null);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const ordersPerPage = 10;

  const handleEditClick = (order) => {
    setSelectedOrder(order);
    setIsEditModalOpen(true);
  };

  const handleModalClose = () => {
    setIsEditModalOpen(false);
    setSelectedOrder(null);
  };

  const handleOrderUpdate = async () => {
    const response = await fetchOrders(currentPage, ordersPerPage);
    setOrders(response.data.docs || []);
  };

  useEffect(() => {
    const fetchOrderList = async () => {
      try {
        const response = await fetchOrders(currentPage, ordersPerPage);
        setOrders(response.data.docs || []);
        setTotalOrders(response.data.totalDocs || 0);
      } catch (err) {
        console.error("Failed to fetch orders:", err);
        setOrders([]);
      }
    };

    fetchOrderList();
  }, [currentPage]);

  const totalPages = Math.ceil(totalOrders / ordersPerPage);

  const getPagination = () => {
    const pages = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push("...");
      for (
        let i = Math.max(2, currentPage - 1);
        i <= Math.min(totalPages - 1, currentPage + 1);
        i++
      ) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) pages.push("...");
      pages.push(totalPages);
    }
    return pages;
  };

  const handleStatusClick = async (orderId, currentStatus) => {
    try {
      const newStatus = currentStatus === "pending" ? "completed" : "pending";
      await orderStatus({ id: orderId, status: newStatus });
      setOrders((prevOrders) =>
        prevOrders.map((order) =>
          order._id === orderId ? { ...order, status: newStatus } : order
        )
      );
    } catch (err) {
      console.error("Failed to update order status:", err.response?.data || err.message);
      alert("Failed to update order status. Please try again.");
    }
  };

  const handleDeleteClick = async (orderId) => {
    try {
      const response = await deleteStatus({ id: orderId });
      setOrders((prevOrders) =>
        prevOrders.filter((order) => order._id !== orderId)
      );

      const counts = await fetchDashboardCount();
      setDashboardCounts({
        totalOrders: counts.data.totalClients || 0,
        pendingOrders: counts.data.pendingOrders || 0,
        operatorPendingOrders: counts.data.pendingOrders || 0,
        completedOrders: counts.data.completedOrders || 0,
      });
    } catch (err) {
      console.error("Failed to delete order:", err.response?.data || err.message);
      alert("Failed to delete order. Please try again.");
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target)
      ) {
        setDropdownIndex(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const [dashboardCounts, setDashboardCounts] = useState({
    totalOrders: 0,
    pendingOrders: 0,
    operatorPendingOrders: 0,
    completedOrders: 0,
  });

  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const counts = await fetchDashboardCount();
        setDashboardCounts({
          totalOrders: counts.data.totalClients || 0,
          pendingOrders: counts.data.pendingOrders || 0,
          operatorPendingOrders: counts.data.awaitingOperator || 0,
          completedOrders: counts.data.completedOrders || 0,
        });
      } catch (err) {
        console.error("Failed to fetch dashboard counts:", err);
      }
    };

    fetchCounts();
  }, []);

  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
  const [selectedComment, setSelectedComment] = useState("");

  const handleViewComments = (comment) => {
    setSelectedComment(comment || "No comments available.");
    setIsCommentModalOpen(true);
  };

  const statusCards = [
    {
      label: "Total Orders",
      count: dashboardCounts.totalOrders,
      color: "bg-blue-100 text-blue-700",
      icon: <FaBox />,
    },
    {
      label: "Pending Orders",
      count: dashboardCounts.pendingOrders,
      color: "bg-yellow-100 text-yellow-700",
      icon: <FaClock />,
    },
    {
      label: "Operator Pending Orders",
      count: dashboardCounts.operatorPendingOrders,
      color: "bg-orange-100 text-orange-700",
      icon: <FaClipboardList />,
    },
    {
      label: "Completed Orders",
      count: dashboardCounts.completedOrders,
      color: "bg-green-100 text-green-700",
      icon: <FaCheckCircle />,
    },
  ];

  return (
    <div className="p-6 px-28 bg-gray-100 text-sm">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {statusCards.map((stat, i) => (
          <div
            key={i}
            className="bg-white shadow rounded-xl p-4 flex items-center gap-4"
          >
            <div className={`${stat.color} p-3 rounded-full text-xl`}>
              {stat.icon}
            </div>
            <div>
              <p className="text-gray-500 text-xs">{stat.label}</p>
              <p className="text-xl font-semibold">{stat.count}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-col md:flex-row md:items-center justify-between mb-4 gap-4">
        <h2 className="text-lg font-semibold">Current Orders</h2>
        <div className="flex gap-2 flex-wrap">
          <input
            type="text"
            placeholder="Search"
            className="border rounded px-3 py-1"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <select
            className="border rounded px-3 py-1"
            value={filter}
            onChange={(e) => {
              setFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option>All Orders</option>
            <option>Pending</option>
            <option>Completed</option>
          </select>
          <button
            className="bg-blue-600 text-white px-4 py-1 rounded"
            onClick={onNewOrder}
          >
            + New Order
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm min-h-[73vh] flex flex-col">
        <div className="flex-1 overflow-auto">
          <table className="min-w-full">
            <thead className="border-b text-left text-l font-semibold text-gray-600 sticky top-0 bg-white">
              <tr>
                <th className="px-4 py-2">Created Date</th>
                <th className="px-4 py-2">Booking Number</th>
                <th className="px-4 py-2">Ocean Vessel</th>
                <th className="px-4 py-2">Call Number</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Action</th>
              </tr>
            </thead>
            <tbody className="text-sm text-gray-700">
              {orders.map((order, i) => (
                <tr key={i} className="text-gray-400">
                  <td className="px-4 py-2">
                    {new Date(order.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-2">{order.shipmentDetails.bookingNumber}</td>
                  <td className="px-4 py-2">{order.shipmentDetails.oceanVessel}</td>
                  <td className="px-4 py-2">{order.shipmentDetails.callNumber}</td>
                  <td className="px-4 py-2">
                    <span
                      className={`cursor-pointer px-2 py-1 rounded text-xs font-semibold ${
                        order.status === "Completed"
                          ? "bg-green-100 text-green-700"
                          : order.status === "Awaiting operator confirmation"
                          ? "bg-red-100 text-orange-600"
                          : "bg-yellow-100 text-yellow-700"
                      }`}
                    >
                      {order.status}
                    </span>
                  </td>
                  <td className="px-4 py-2 relative">
                    <button
                      onClick={() =>
                        setDropdownIndex(dropdownIndex === i ? null : i)
                      }
                      className="text-lg hover:text-gray-700"
                    >
                      ⋯
                    </button>
                    {dropdownIndex === i && (
                      <div
                        ref={dropdownRef}
                        className="absolute right-2 top-10 z-10 bg-white shadow rounded w-28 text-sm"
                      >
                        {new Date() - new Date(order.createdAt) <= 48 * 60 * 60 * 1000 && (
                          <button
                            className="w-full px-4 py-2 text-left hover:bg-gray-100"
                            onClick={() => {
                              handleEditClick(order);
                              setDropdownIndex(null);
                            }}
                          >
                            Edit
                          </button>
                        )}
                        <button
                          className="w-full px-4 py-2 text-left hover:bg-gray-100"
                          onClick={() => {
                            handleDeleteClick(order._id);
                            setDropdownIndex(null);
                          }}
                        >
                          Delete
                        </button>
                        <button
                          className="w-full px-4 py-2 text-left hover:bg-gray-100"
                          onClick={() => handleViewComments(order.comment)}
                        >
                          View Comments
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination and Logout at bottom of table */}
        <div className="border-t bg-white p-4 flex justify-between items-center">
          <button
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
            onClick={async () => {
              try {
                await logoutUser();
              } catch (err) {
                console.error("Logout failed:", err);
              } finally {
                localStorage.clear();
                window.location.href = "/";
              }
            }}
          >
            Logout
          </button>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 border rounded disabled:text-gray-300"
            >
              Prev
            </button>
            {getPagination().map((item, idx) => (
              <button
                key={idx}
                onClick={() => typeof item === "number" && setCurrentPage(item)}
                disabled={item === "..."}
                className={`w-8 h-8 rounded-full text-sm ${item === currentPage
                  ? "bg-blue-600 text-white"
                  : "bg-white border text-gray-700"
                  }`}
              >
                {item}
              </button>
            ))}
            <button
              onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 border rounded disabled:text-gray-300"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {isEditModalOpen && (
        <EditOrderModal
          orderData={selectedOrder}
          onClose={handleModalClose}
          onUpdate={handleOrderUpdate}
        />
      )}

      {isCommentModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg w-full max-w-md">
            <h2 className="text-lg font-semibold mb-4">Comment</h2>
            <p className="text-gray-700">{selectedComment}</p>
            <div className="flex justify-end mt-4">
              <button
                onClick={() => setIsCommentModalOpen(false)}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardData;
